import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form } from '@amzn/storm-ui-column-form-kit';
import { useQuery } from '@tanstack/react-query';

import { ErrorDisplay } from '../../../../../Shared/components/ErrorDisplay/ErrorDisplay';
import { UserRoles } from '../../../../../Shared/constants/userRoles';
import { ApiError } from '../../../../../Shared/interfaces/ApiError';
import useApiClient from '../../../../../Shared/services/api';
import { useAppContext } from '../../../../../Shared/store/appContext';
import CustomSpinner from '../../../../../UiComponentsLibrary/CustomSpinner';
import { getSlotPackagesData } from '../../../../services/helpers';
import { EndpointQueryKeys } from '../../../../constants/endpoints';
import {
  getAccountExecutives,
  getAccountManagers,
  getAdvertisers,
  getTypeOfPurchaseList,
} from '../../../../services/service';
import { validateBookingFields } from '../../../QueueSheetManagement/utils/bookingFieldValidation';
import { createFormConfig } from '../../config/formConfig';
import {
  FORM_FIELDS,
  ADVERTISER_NOT_FOUND_STRING,
} from '../../constants/FormFieldsConstants';
import {
  AccountExecutivesResponse,
  AccountManagersResponse,
  Advertiser,
  AdvertisersResponse,
  AEUser,
  TypeOfPurchasesResponse,
} from '../../interface/ApiInterfaces';
import {
  BookingFormData,
  BookingFormDataErrors,
  CreateBookingFormProps,
} from '../../interface/BookingFormInterface';
import { SlotPackage } from '../../interface/DetailedBookingInterface';
import { FormFieldWrapper } from '../FormFieldWrapper';
import { SHARED_CONSTANTS } from '../../../../../Shared/constants/commonConstants';

const CreateBookingForm = ({
  initialData,
  onFormSubmit,
  validateFormTrigger,
  isEditMode = false,
}: CreateBookingFormProps) => {
  const api = useApiClient();
  const { t } = useTranslation();
  const { state } = useAppContext();
  const artEventId = state.selectedArtEventId ?? '';
  const shouldPrefillUser = !isEditMode && !initialData;

  const getValueToFill = (roleToCheck: UserRoles) => {
    return shouldPrefillUser && state.currentUser?.role === roleToCheck
      ? state.currentUser.userId
      : '';
  };

  const [formData, setFormData] = useState<BookingFormData>(() => {
    return {
      aeName:
        initialData?.aeName || getValueToFill(UserRoles.ACCOUNT_EXECUTIVE),
      amName: initialData?.amName || getValueToFill(UserRoles.ACCOUNT_MANAGER),
      category: initialData?.category || '',
      org: initialData?.org || '',
      rodeoAdName: initialData?.rodeoAdName || '',
      brand: initialData?.brand || '',
      typeOfPurchase: initialData?.typeOfPurchase || '',
      exactBillingValue: initialData?.exactBillingValue ?? 0,
      sfOpportunityId: initialData?.sfOpportunityId || '',
      ro: initialData?.ro || '',
      brandConfirmation: initialData?.brandConfirmation || '',
      commentsByPoc: initialData?.commentsByPoc || '',
      creatives: initialData?.creatives || '',
      gtmStatus: initialData?.gtmStatus || '',
    };
  });

  const [fieldErrors, setFieldErrors] = useState<BookingFormDataErrors>({
    aeName: '',
    amName: '',
    category: '',
    org: '',
    rodeoAdName: '',
    brand: '',
    typeOfPurchase: '',
    exactBillingValue: '',
    sfOpportunityId: '',
    ro: '',
    brandConfirmation: '',
    commentsByPoc: '',
    creatives: '',
    gtmStatus: '',
  });

  const previousValidateFormTriggerRef = useRef<boolean | undefined>(undefined);
  const hasUserTriggeredValidation = useRef<boolean>(false);

  const {
    data: slotPackagesResponse,
    isLoading: isSlotPackagesLoading,
    isError: isSlotPackagesError,
  } = getSlotPackagesData(api, formData.typeOfPurchase, artEventId);

  const slotPackages = slotPackagesResponse?.slotPackages || [];

  const validateForm = useCallback(() => {
    const errors = validateBookingFields(formData, t, isEditMode);

    const hasErrors = Object.values(errors).some((error) => error !== '');

    if (hasUserTriggeredValidation.current) setFieldErrors(errors);

    if (!formData.typeOfPurchase || !formData.category || !artEventId) {
      return { isValid: !hasErrors };
    }

    if (isSlotPackagesLoading) {
      return { isValid: false };
    }

    if (isSlotPackagesError) {
      return { isValid: !hasErrors };
    }

    const filteredPackages = slotPackages
      .map((pkg: SlotPackage) => ({
        ...pkg,
        segments: pkg.segments.filter((seg) =>
          seg.category
            ?.toLowerCase()
            .split(',')
            .some((c) => c.trim() === formData.category.toLowerCase())
        ),
      }))
      .filter((pkg: SlotPackage) => pkg.segments.length > 0);

    if (filteredPackages.length === 0) {
      setFieldErrors((prev) => ({
        ...prev,
        typeOfPurchase: t('no-inventory-available'),
      }));
      return { isValid: false };
    }

    return { isValid: !hasErrors, filteredPackages };
  }, [
    formData,
    isEditMode,
    slotPackages,
    isSlotPackagesLoading,
    isSlotPackagesError,
    artEventId,
  ]);

  useEffect(() => {
    if (initialData) {
      const newFormData = {
        aeName: initialData.aeName || '',
        amName: initialData.amName || '',
        category: initialData.category || '',
        org: initialData.org || '',
        rodeoAdName: initialData.rodeoAdName || '',
        brand: initialData.brand || '',
        typeOfPurchase: initialData.typeOfPurchase || '',
        exactBillingValue: initialData.exactBillingValue ?? 0,
        sfOpportunityId: initialData.sfOpportunityId || '',
        ro: initialData.ro || '',
        brandConfirmation: initialData.brandConfirmation || '',
        commentsByPoc: initialData.commentsByPoc || '',
        creatives: initialData.creatives || '',
        gtmStatus: initialData.gtmStatus || '',
      };

      setFormData(newFormData);
    }
  }, [initialData]);

  useEffect(() => {
    if (validateFormTrigger !== previousValidateFormTriggerRef.current) {
      if (previousValidateFormTriggerRef.current !== undefined)
        hasUserTriggeredValidation.current = true;

      previousValidateFormTriggerRef.current = validateFormTrigger;
      if (hasUserTriggeredValidation.current) {
        const { isValid, filteredPackages } = validateForm();
        if (isValid && onFormSubmit) {
          onFormSubmit({
            ...formData,
            filteredSlotPackages: filteredPackages,
          });
        }
      }
    }
  }, [validateFormTrigger, validateForm, onFormSubmit, formData]);

  const {
    data: aeUsersResponse,
    error: aeUsersError,
    isError: isAeUsersError,
    isLoading: isAeUsersLoading,
  } = useQuery<AccountExecutivesResponse, ApiError>({
    queryKey: [EndpointQueryKeys.GET_ACCOUNT_EXECUTIVES],
    queryFn: () => getAccountExecutives(api),
    enabled: !!api,
    staleTime: SHARED_CONSTANTS.STALE_TIME_FOR_REACT_QUERY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const {
    data: amUsersResponse,
    error: amUsersError,
    isError: isAmUsersError,
    isLoading: isAmUsersLoading,
  } = useQuery<AccountManagersResponse, ApiError>({
    queryKey: [EndpointQueryKeys.GET_ACCOUNT_MANAGERS],
    queryFn: () => getAccountManagers(api),
    enabled: !!api,
    staleTime: SHARED_CONSTANTS.STALE_TIME_FOR_REACT_QUERY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const {
    data: typeOfPurchaseResponse,
    error: typeOfPurchaseError,
    isError: isTypeOfPurchaseError,
    isLoading: isTypeOfPurchaseLoading,
  } = useQuery<TypeOfPurchasesResponse, ApiError>({
    queryKey: [EndpointQueryKeys.GET_TYPE_OF_PURCHASE_LIST],
    queryFn: () => getTypeOfPurchaseList(api),
    enabled: !!api,
    staleTime: SHARED_CONSTANTS.STALE_TIME_FOR_REACT_QUERY,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const {
    data: advertisersResponse,
    error: advertisersError,
    isError: isAdvertisersError,
  } = useQuery<AdvertisersResponse, ApiError>({
    queryKey: [
      EndpointQueryKeys.GET_ADVERTISERS,
      formData.category,
      formData.org,
    ],
    queryFn: () => getAdvertisers(api, formData.category, formData.org),
    enabled: !!api && !!formData.category && !!formData.org,
  });

  const aeUsers = aeUsersResponse?.accountExecutives || [];
  const amUsers = amUsersResponse?.accountManagers || [];
  const typeOfPurchaseList = typeOfPurchaseResponse?.typeOfPurchases || [];
  const advertisersList = advertisersResponse?.advertisers || [];

  useEffect(() => {
    if (
      !isEditMode &&
      formData.aeName &&
      !formData.category &&
      aeUsers.length > 0
    ) {
      const selectedUser = aeUsers.find(
        (user: AEUser) => user.userId === formData.aeName
      );
      if (selectedUser)
        setFormData((prev) => ({
          ...prev,
          category: selectedUser.category,
          org: selectedUser.orgName || '',
        }));
    }
  }, [formData.aeName, aeUsers, isEditMode, formData.category]);

  useEffect(() => {
    if (hasUserTriggeredValidation.current) {
      validateForm();
    }
  }, [formData, validateForm]);

  if (isAeUsersLoading || isAmUsersLoading || isTypeOfPurchaseLoading)
    return <CustomSpinner />;

  if (isAeUsersError) return <ErrorDisplay error={aeUsersError} />;

  if (isAmUsersError) return <ErrorDisplay error={amUsersError} />;

  if (isTypeOfPurchaseError)
    return <ErrorDisplay error={typeOfPurchaseError} />;

  if (isAdvertisersError) return <ErrorDisplay error={advertisersError} />;

  const handleFieldChange = (field: keyof BookingFormData, value: string) => {
    if (field === FORM_FIELDS.EXACT_BILLING_VALUE) {
      const numericValue = value === '' ? 0 : parseFloat(value) || 0;
      setFormData((prev) => ({ ...prev, [field]: numericValue }));
    } else setFormData((prev) => ({ ...prev, [field]: value }));

    if (
      field === FORM_FIELDS.TYPE_OF_PURCHASE ||
      field === FORM_FIELDS.CATEGORY
    )
      setFieldErrors((prev) => ({
        ...prev,
        typeOfPurchase:
          prev.typeOfPurchase === t('no-inventory-available')
            ? ''
            : prev.typeOfPurchase,
      }));
  };

  const handleAeNameChange = (value: string) => {
    handleFieldChange(FORM_FIELDS.AE_NAME, value);

    const selectedUser = aeUsers.find((user: AEUser) => user.userId === value);
    if (selectedUser) {
      const categoryChanged = selectedUser.category !== formData.category;
      handleFieldChange(FORM_FIELDS.CATEGORY, selectedUser.category);

      // Auto-select org from AE's orgName
      const aeOrg = selectedUser.orgName || '';
      const orgChanged = aeOrg !== formData.org;
      handleFieldChange(FORM_FIELDS.ORG, aeOrg);

      if (categoryChanged || orgChanged) {
        handleFieldChange(FORM_FIELDS.RODEO_AD_NAME, '');
        handleFieldChange(FORM_FIELDS.BRAND, '');
      }
    }
  };

  const handleRodeoAdvertiserChange = (value: string) => {
    handleFieldChange(FORM_FIELDS.RODEO_AD_NAME, value);

    if (value === ADVERTISER_NOT_FOUND_STRING)
      // Clear brand field to allow user input
      handleFieldChange(FORM_FIELDS.BRAND, '');
    else {
      const selectedAdvertiser = advertisersList.find(
        (adv: Advertiser) => adv.advertiserName === value
      );
      if (selectedAdvertiser)
        handleFieldChange(FORM_FIELDS.BRAND, selectedAdvertiser.brandName);
      else handleFieldChange(FORM_FIELDS.BRAND, '');
    }
  };

  const formConfig = createFormConfig(
    formData,
    {
      handleAeNameChange,
      handleRodeoAdvertiserChange,
      handleFieldChange,
    },
    { aeUsers, amUsers, typeOfPurchaseList, advertisersList },
    fieldErrors,
    isEditMode,
    t,
    state.currentUser?.role
  );

  return (
    <Form id="booking-form">
      {formConfig.map((field) => (
        <FormFieldWrapper
          key={field.id}
          id={field.id}
          type={field.type}
          label={field.label}
          value={field.value}
          onChange={field.onChange}
          placeholder={field.placeholder}
          disabled={field.disabled}
          optional={field.optional}
          options={field.options}
          persistentBottomOption={field.persistentBottomOption}
          maxLength={field.maxLength}
          validation={field.validation}
          error={field.error}
        />
      ))}
    </Form>
  );
};

CreateBookingForm.displayName = 'CreateBookingForm';
export default CreateBookingForm;
